import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material'
import { FC, Fragment, ReactNode, useEffect, useRef } from 'react'
import { GuidelineBlockDTO, GuidelineBlockType, GuidelineRichTextDTO } from 'models/guidelines'

import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded'
import { logger } from 'utils/logging'
import styles from './GuidelinesRenderer.module.sass'

interface Props {
  blocks: GuidelineBlockDTO[]
}

// Notion guideline content is authored outside the codebase, so URLs in it are untrusted.
const SAFE_LINK_SCHEMES = new Set(['http:', 'https:', 'mailto:'])

// Returns the href only when it uses a safe scheme, dropping javascript:/data: etc. so a
// malicious guideline link can't execute script in the authenticated platform origin.
const safeHref = (href: string | null | undefined): string | undefined => {
  if (!href) return undefined
  try {
    return SAFE_LINK_SCHEMES.has(new URL(href, window.location.origin).protocol) ? href : undefined
  } catch {
    return undefined
  }
}

// Only allow http(s) media sources for img/video tags loaded from Notion content.
const safeMediaSrc = (src: string | null | undefined): string | undefined => {
  if (!src) return undefined
  try {
    const { protocol } = new URL(src, window.location.origin)
    return protocol === 'http:' || protocol === 'https:' ? src : undefined
  } catch {
    return undefined
  }
}

// YouTube embed ids are exactly 11 url-safe chars; reject anything else so the value
// can't manipulate the iframe target (path/query/userinfo tricks).
const YOUTUBE_VIDEO_ID_PATTERN = /^[A-Za-z0-9_-]{11}$/

const KNOWN_BLOCK_TYPES = new Set<string>(Object.values(GuidelineBlockType))

// Collects unsupported block types whose content we actually drop — a BE-mapped UNKNOWN (or a new
// Notion type this FE doesn't handle) with no children to fall back to — so content loss is
// observable in production. Blocks we recover by rendering their children are not flagged.
const collectUnsupportedBlockTypes = (blocks: GuidelineBlockDTO[], acc: Set<string> = new Set<string>()): Set<string> => {
  blocks.forEach(block => {
    const isUnsupported = block.type === GuidelineBlockType.UNKNOWN || !KNOWN_BLOCK_TYPES.has(block.type)

    if (isUnsupported && !block.children?.length) acc.add(block.type)
    if (block.children?.length) collectUnsupportedBlockTypes(block.children, acc)
  })
  return acc
}

/**
 * Renders the structured Notion-page tree returned by
 * `GET /guidelines/{productKind}` (backend ISSUE-1675).
 * Handles all 18 block types plus inline rich-text styling.
 */
export const GuidelinesRenderer: FC<Props> = ({ blocks }) => {
  useEffect(() => {
    const unsupported = [...collectUnsupportedBlockTypes(blocks)]

    if (unsupported.length) {
      logger.warn('Notion guideline block type(s) dropped (unsupported, no renderable children)', { unsupportedTypes: unsupported }, { context: { component: 'GuidelinesRenderer' } })
    }
  }, [blocks])

  return <div className={styles.root}>{renderBlocks(blocks)}</div>
}

function renderBlocks(blocks: GuidelineBlockDTO[]): ReactNode {
  const out: ReactNode[] = []
  let i = 0

  while (i < blocks.length) {
    const block = blocks[i]

    if (block.type === GuidelineBlockType.BULLETED_LIST_ITEM || block.type === GuidelineBlockType.NUMBERED_LIST_ITEM) {
      const groupType = block.type
      const groupItems: GuidelineBlockDTO[] = []

      while (i < blocks.length && blocks[i].type === groupType) {
        groupItems.push(blocks[i])
        i++
      }

      const ListTag = groupType === GuidelineBlockType.BULLETED_LIST_ITEM ? 'ul' : 'ol'

      out.push(
        <ListTag key={groupItems[0].id} className={styles.list}>
          {groupItems.map(item => (
            <li key={item.id}>

              <RichText spans={item.content?.richText} />

              {item.children.length > 0 && renderBlocks(item.children)}

            </li>
          ))}
        </ListTag>
      )
      continue
    }

    out.push(<BlockNode key={block.id} block={block} />)
    i++
  }

  return out
}

const BlockNode: FC<{ block: GuidelineBlockDTO }> = ({ block }) => {
  const { type, content, children } = block

  switch (type) {
    case GuidelineBlockType.PARAGRAPH:
      return (
        <p className={styles.paragraph}>
          <RichText spans={content?.richText} />
        </p>
      )

    case GuidelineBlockType.HEADING_1:
      return <HeadingBlock level={1} content={content} childBlocks={children} />

    case GuidelineBlockType.HEADING_2:
      return <HeadingBlock level={2} content={content} childBlocks={children} />

    case GuidelineBlockType.HEADING_3:
      return <HeadingBlock level={3} content={content} childBlocks={children} />

    case GuidelineBlockType.TO_DO:
      return (
        <div className={styles.todo}>

          <input type="checkbox" checked={!!content?.checked} readOnly />

          <span className={content?.checked ? styles.todoChecked : undefined}>
            <RichText spans={content?.richText} />
          </span>

        </div>
      )

    case GuidelineBlockType.CALLOUT:
      return (
        <div className={styles.callout}>

          {content?.icon && <span className={styles.calloutIcon} aria-hidden>{content.icon}</span>}

          <div className={styles.calloutBody}>

            <RichText spans={content?.richText} />

            {children.length > 0 && renderBlocks(children)}

          </div>

        </div>
      )

    case GuidelineBlockType.IMAGE: {
      const src = safeMediaSrc(content?.imageUrl)
      return src
        ? <img className={styles.image} src={src} alt="" loading="lazy" />
        : null
    }

    case GuidelineBlockType.VIDEO: {
      const src = safeMediaSrc(content?.videoUrl)
      return src
        ? <video className={styles.video} src={src} controls preload="metadata" />
        : null
    }

    case GuidelineBlockType.YOUTUBE:
      return content?.youtubeVideoId && YOUTUBE_VIDEO_ID_PATTERN.test(content.youtubeVideoId)
        ? (
          <div className={styles.youtube}>
            <iframe
              src={`https://www.youtube.com/embed/${content.youtubeVideoId}`}
              title="YouTube video"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              loading="lazy"
            />
          </div>
        )
        : null

    case GuidelineBlockType.DIVIDER:
      return <hr className={styles.divider} />

    case GuidelineBlockType.TABLE: {
      const rows = children.filter(c => c.type === GuidelineBlockType.TABLE_ROW)
      const hasColumnHeader = !!content?.hasColumnHeader
      const hasRowHeader = !!content?.hasRowHeader

      return (
        <table className={styles.table}>

          {hasColumnHeader && rows.length > 0 && (
            <thead>
              <tr>
                {(rows[0].content?.cells ?? []).map((cell, ci) => (
                  <th key={ci}><RichText spans={cell} /></th>
                ))}
              </tr>
            </thead>
          )}

          <tbody>
            {rows.slice(hasColumnHeader ? 1 : 0).map(row => (
              <tr key={row.id}>
                {(row.content?.cells ?? []).map((cell, ci) => {
                  const Cell = hasRowHeader && ci === 0 ? 'th' : 'td'
                  return <Cell key={ci}><RichText spans={cell} /></Cell>
                })}
              </tr>
            ))}
          </tbody>

        </table>
      )
    }

    case GuidelineBlockType.TABLE_ROW:
      return null

    case GuidelineBlockType.COLUMN_LIST:
      return (
        <div className={styles.columnList}>
          {children.map(col => (
            <div key={col.id} className={styles.column}>
              {renderBlocks(col.children)}
            </div>
          ))}
        </div>
      )

    case GuidelineBlockType.COLUMN:
      return (
        <div className={styles.column}>
          {renderBlocks(children)}
        </div>
      )

    case GuidelineBlockType.SLIDESHOW: {
      const images = (content?.slideshowImages ?? []).map(safeMediaSrc).filter((src): src is string => !!src)
      return images.length
        ? (
          <div className={styles.slideshow}>
            {images.map((src, i) => (
              <img key={i} src={src} alt="" loading="lazy" />
            ))}
          </div>
        )
        : null
    }

    case GuidelineBlockType.BEFORE_AFTER: {
      const before = safeMediaSrc(content?.beforeAfterImages?.before)
      const after = safeMediaSrc(content?.beforeAfterImages?.after)
      return before || after
        ? (
          <div className={styles.beforeAfter}>

            {before &&
              <figure>
                <img src={before} alt="Before" loading="lazy" />
                <figcaption>Before</figcaption>
              </figure>
            }

            {after &&
              <figure>
                <img src={after} alt="After" loading="lazy" />
                <figcaption>After</figcaption>
              </figure>
            }

          </div>
        )
        : null
    }

    case GuidelineBlockType.UNKNOWN:
    default:
      // Notion can wrap real content (synced blocks, list/column wrappers, etc.) in a node the BE
      // maps to UNKNOWN. Render its children when present rather than dropping the whole subtree;
      // a genuinely empty unsupported block renders nothing and is reported by the effect below.
      return children.length > 0 ? <>{renderBlocks(children)}</> : null
  }
}

interface HeadingBlockProps {
  level: 1 | 2 | 3
  content: GuidelineBlockDTO['content']
  childBlocks: GuidelineBlockDTO[]
}

// QA-243: `timeout="auto"` defers `onEntered` until the expand transition finishes, so two
// quickly-opened toggles would otherwise race their scroll corrections (last-to-finish wins).
// Each expansion takes a fresh token; only the toggle still holding the latest one may scroll.
let latestExpansionToken = 0

// Notion "toggle headings" arrive as HEADING_* blocks with their content in `children`.
// Render with native <details> (closed by default) when there are children, so the FE mirrors Notion's accordion behavior.
const HeadingBlock: FC<HeadingBlockProps> = ({ level, content, childBlocks }) => {
  const HeadingTag = (`h${level}` as 'h1' | 'h2' | 'h3')
  const headingClass = level === 1 ? styles.heading1 : level === 2 ? styles.heading2 : styles.heading3
  const summaryRef = useRef<HTMLDivElement>(null)
  const expansionTokenRef = useRef(0)

  if (childBlocks.length === 0) {
    return (
      <HeadingTag className={headingClass}>
        <RichText spans={content?.richText} />
      </HeadingTag>
    )
  }

  return (
    <Accordion
      disableGutters
      elevation={0}
      square
      className={styles.toggle}
      sx={{
        backgroundColor: 'transparent',
        '&:before': { display: 'none' }
      }}
      // QA-243: with the header near the viewport top, browser scroll anchoring can pin the
      // content *below* the toggle while it expands, jumping the revealed content above the
      // visible area. Once the expand transition finishes, bring the summary header back into
      // view — the header (not the whole accordion, which can be taller than the viewport and
      // then never scrolls with `block: 'nearest'`) is always viewport-sized, so 'nearest'
      // performs the upward correction in every case. Skipped when another toggle has been
      // opened since (stale token), so competing corrections don't fight each other.
      onChange={(_, expanded) => {
        if (expanded) expansionTokenRef.current = ++latestExpansionToken
      }}
      TransitionProps={{
        onEntered: () => {
          if (expansionTokenRef.current !== latestExpansionToken) return
          summaryRef.current?.scrollIntoView?.({ block: 'nearest' })
        }
      }}
    >

      <AccordionSummary
        ref={summaryRef}
        expandIcon={<KeyboardArrowDownRoundedIcon className={styles.toggleIcon} />}
        className={styles.toggleSummary}
        sx={{
          padding: 0,
          minHeight: 0,
          '& .MuiAccordionSummary-content': { margin: 0 }
        }}
      >
        <HeadingTag className={headingClass} style={{ margin: 0 }}>
          <RichText spans={content?.richText} />
        </HeadingTag>
      </AccordionSummary>

      <AccordionDetails className={styles.toggleBody}>
        {renderBlocks(childBlocks)}
      </AccordionDetails>

    </Accordion>
  )
}

const RichText: FC<{ spans: GuidelineRichTextDTO[] | null | undefined }> = ({ spans }) => {
  if (!spans?.length) return null

  return (
    <>
      {spans.map((span, i) => {
        let node: ReactNode = span.text

        if (span.code) node = <code className={styles.rtCode}>{node}</code>
        if (span.bold) node = <strong className={styles.rtBold}>{node}</strong>
        if (span.italic) node = <em className={styles.rtItalic}>{node}</em>
        if (span.strikethrough) node = <span className={styles.rtStrike}>{node}</span>
        if (span.underline) node = <span className={styles.rtUnderline}>{node}</span>

        const href = safeHref(span.href)
        if (href) {
          node = (
            <a className={styles.rtLink} href={href} target="_blank" rel="noopener noreferrer">
              {node}
            </a>
          )
        }

        return <Fragment key={i}>{node}</Fragment>
      })}
    </>
  )
}
